import React, { useEffect, useState } from 'react';
import { BaseWizardProps } from '../wizard/wizard-step-base';
import { AppDispatch, RootState, store } from '../../state/store';
import { useDispatch, useSelector } from 'react-redux';
import {
  GoACalendar,
  GoACallout,
  GoACheckbox,
  GoADatePicker,
  GoAFormItem,
  GoAInputDate,
  GoAInputText,
  GoASpacer,
} from '@abgov/react-components';
import {
  defaultValidate,
  WizardButtonGroup,
} from '../wizard/wizard-button-group-component';
import { REACT_DEFAULT_DATE } from '../../state/configuration';
import { set } from 'lodash';
import { updateCollision } from '../../state/slices/collisionSlice';
import { formatProdErrorMessage } from '@reduxjs/toolkit';
import { TimePickerComponent } from './time-picker-component';

import styles from './datetime.module.css';
import {
  pushNotification,
  removeAllValidationErrorNotifications,
} from '../../state/slices/notificationSlice';
import { NotificationType } from '../notifications/notification-component';
import {
  convert12HourTimeTo24HourTime,
  formatDateToISOWithTimezone,
} from '../../common/validation';
import _ from 'lodash';
import { Collision } from '../../models/collision';
import { isHitAndRun } from '../../state/slices/configurationSlice';
import { c } from 'vite/dist/node/types.d-aGj9QkWt';
import { displaySection } from '../../common/ui-library';
import moment from 'moment';

interface DateTimeProps extends BaseWizardProps {}

const useAppDispatch = () => useDispatch<AppDispatch>();
const useAppSelector: <TSelected>(
  selector: (state: RootState) => TSelected,
) => TSelected = useSelector;

export const DateTimeComponent: React.FC<DateTimeProps> = ({
  navigationConfiguration,
}) => {
  const dispatch = useAppDispatch();

  const stateCollision = useAppSelector((state: RootState) => state.collision);
  const stateConfiguration = useAppSelector(
    (state: RootState) => state.configuration,
  );

  const [nextPage, setNextPage] = useState('');

  const dateErrorText = 'Choose a collision date';
  const timeErrorText = 'Choose a collision time';
  const amPmErrorText = 'Choose AM or PM in order to continue';
  const dateBeforeGoLiveError =
    `Choose a date on or after ` + moment(REACT_DEFAULT_DATE).format('MMM Do, YYYY') + ` to continue`;
  const dateInTheFutureError =
    'Choose a date that is not in the future to continue';
  const timeInFutureError =
    'Choose a time that is not in the future to continue';

  const [dateFormError, setDateFormError] = useState('');
  const [timeFormError, setTimeFormError] = useState('');
  const [dateValue, setDateValue] = useState<Date | undefined>();
  const [timeValue, setTimeValue] = useState<string>(
    stateCollision.collision.timeOfCollision ?? '',
  );

  const regex = /^(\d{1,2}):(\d{2})\s?(AM|PM)$/i;
  const [hours, setHours] = useState(
    stateCollision.collision.timeOfCollision &&
      stateCollision.collision.timeOfCollision.match(regex)
      ? parseInt(stateCollision.collision.timeOfCollision.match(regex)![1])
      : 0,
  );
  const [minutes, setMinutes] = useState(
    stateCollision.collision.timeOfCollision &&
      stateCollision.collision.timeOfCollision.match(regex)
      ? parseInt(stateCollision.collision.timeOfCollision.match(regex)![2])
      : 0,
  );
  const [period, setPeriod] = useState(
    stateCollision.collision.timeOfCollision &&
      stateCollision.collision.timeOfCollision.match(regex)
      ? stateCollision.collision.timeOfCollision.match(regex)![3].toUpperCase()
      : '',
  );

  const [dateUnknown, setDateUnknown] = useState(
    stateCollision.collision.dateUnknown ?? false,
  );
  const [timeUnknown, setTimeUnknown] = useState(
    stateCollision.collision.timeUnknown ?? false,
  );

  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (stateCollision.collision.dateOfCollision) {
      setDateValue(new Date(stateCollision.collision.dateOfCollision));
    }
  }, [stateCollision.collision.dateOfCollision]);

  useEffect(() => {
    const pageConfig = navigationConfiguration.pagingConfiguration.find(
      (item) => item.default !== undefined,
    );

    setNextPage(pageConfig?.default ?? 'error');
  }, []);

  useEffect(() => {
    if (dateValue) {
      setDateUnknown(false);
    }
  }, [dateValue]);

  useEffect(() => {
    if (timeValue) {
      setTimeUnknown(false);
    }
  }, [timeValue]);

  useEffect(() => {
    if (timeUnknown) {
      setHours(0);
      setMinutes(0);
      setPeriod('');
      setTimeValue('');
    }
  }, [timeUnknown]);

  const getDateTimeNotificationError = (
    isDateValid: boolean,
    isTimeValid: boolean,
  ): string => {
    if (!isDateValid || !isTimeValid) {
      return 'Choose a date and time below.';
    }

    if (!isDateValid || !isTimeValid) {
      return 'You must either choose a date and time';
    }

    return '';
  };

  const validateDate = (): boolean => {
    if (!dateValue && !dateUnknown) {
      setDateFormError(dateErrorText);
      return false;
    }

    setDateFormError('');
    return true;
  };

  const validateTime = (): boolean => {
    if (!timeValue) {
      setTimeFormError(timeErrorText);
      return false;
    }

    setTimeFormError('');
    return true;
  };

  const validate = (): boolean => {
    dispatch(removeAllValidationErrorNotifications());

    let valid = true;
    let dateValid = validateDate();
    let timeValid = validateTime();

    let [time, period] = timeValue.split(' ');
    let [hours, minutes] = time.split(':').map(Number);

    if (dateValue) {
      const todayDate = new Date();
      todayDate.setHours(0, 0, 0, 0);

      //validate we didnt enter a date in the past manually that was before go live default date
      if (dateValue && dateValue < new Date(REACT_DEFAULT_DATE)) {
        setDateFormError(dateBeforeGoLiveError);
        dispatch(
          pushNotification({
            body: dateBeforeGoLiveError,
            notificationType: NotificationType.importantValidationError,
          }),
        );
        valid = false;
      }

      // validate we didnt enter a date in the future manually

      if (dateValue && dateValue > todayDate) {
        setDateFormError(dateInTheFutureError);
        dispatch(
          pushNotification({
            body: dateInTheFutureError,
            notificationType: NotificationType.importantValidationError,
          }),
        );
        valid = false;
      }

      // validate time is not in future but the date is today
      const todayTime = new Date();

      if (
        dateValue.getFullYear() === todayTime.getFullYear() &&
        dateValue.getMonth() === todayTime.getMonth() &&
        dateValue.getDate() === todayTime.getDate()
      ) {
        const convertedTime = convert12HourTimeTo24HourTime(timeValue);

        if (
          convertedTime.getHours() > todayTime.getHours() ||
          (convertedTime.getHours() === todayTime.getHours() &&
            convertedTime.getMinutes() > todayTime.getMinutes())
        ) {
          setTimeFormError(timeInFutureError);
          dispatch(
            pushNotification({
              body: timeInFutureError,
              notificationType: NotificationType.importantValidationError,
            }),
          );
          valid = false;
        }
      }
    }

    if (!dateValid) {
      valid = false;
      setDateFormError(dateErrorText);
    }

    if (!timeValid) {
      valid = false;
      setTimeFormError(timeErrorText);
    }

    if (!timeValid || !dateValid) {
      dispatch(
        pushNotification({
          body: getDateTimeNotificationError(dateValid, timeValid),
          notificationType: NotificationType.importantValidationError,
        }),
      );
    }

    return valid;
  };

  const save = () => {
    let updatedCollision: Collision = { ...stateCollision.collision };

    updatedCollision.dateOfCollision = dateValue
      ? formatDateToISOWithTimezone(dateValue)
      : undefined;
    updatedCollision.dateUnknown = dateUnknown;
    updatedCollision.timeOfCollision = timeValue;
    updatedCollision.timeUnknown = timeUnknown;

    if (
      navigationConfiguration.updateMetadata &&
      navigationConfiguration.stepId
    ) {
      const stepUpdated = navigationConfiguration.updateMetadata(
        updatedCollision,
        navigationConfiguration.stepId!,
        navigationConfiguration.stageCompleted
          ? navigationConfiguration.stageCompleted
          : false,
        !navigationConfiguration.stageCompleted,
      );

      if (stepUpdated && updatedCollision.metadata) {
        updatedCollision.metadata = {
          ...updatedCollision.metadata,
          formState: stepUpdated,
        };
      }
    }

    dispatch(updateCollision(updatedCollision));

    if (navigationConfiguration.save) {
      navigationConfiguration.save();
    }
  };

  return (
    <div>
      <h1>Collision date and time</h1>
      <p>
        Select the date and time of the collision below. Currently, this service
        only accepts reports for collisions from May 21, 2025 onward. Future
        dates are not allowed.
      </p>
      <div
        style={{
          display: displaySection(
            isHitAndRun(
              stateCollision.collision.collisionType!,
              stateConfiguration,
            ),
          ),
        }}
      >
        <GoACallout
          type="information"
          heading="Important for hit and run collisions"
        >
          Enter an approximate date and time if you are unsure of exactly when
          the collision occurred.
        </GoACallout>
      </div>
      <GoAFormItem label="Choose collision date" error={dateFormError}>
        <div style={{ zIndex: '1002' }}>
          <GoAInputDate
            name="datePicker"
            value={dateValue}
            onChange={(_, value) => {
              if (value) {
                setDateValue(new Date(value));
                setDateFormError('');
              }
            }}
            disabled={dateUnknown}
            placeholder=""
            min={
              (() => {
                const minDate = new Date(REACT_DEFAULT_DATE);
                minDate.setDate(minDate.getDate() + 1);
                return minDate;
              })()
            }
            max={new Date()}
            error={dateFormError.length > 0}
          ></GoAInputDate>
        </div>
      </GoAFormItem>
      <GoASpacer vSpacing="l" />
      <div
        style={{ display: isModalOpen ? 'block' : 'none' }}
        className={styles.overlay}
      ></div>
      <div style={{ zIndex: isModalOpen ? 1001 : 0 }} className={styles.modal}>
        <GoAFormItem label="Choose collision time" error={timeFormError}>
          <TimePickerComponent
            hours={hours}
            minutes={minutes}
            period={period}
            onChange={(timeString) => {
              setTimeValue(timeString);
              setTimeFormError('');
            }}
            onClose={() => {
              setIsModalOpen(false);
            }}
            onOpen={() => {
              setIsModalOpen(true);
            }}
            error={timeFormError.length > 0}
            disabled={timeUnknown}
            setCustomError={(error) => {
              // this is for the am pm error cases that extends the normal validation flow
              setTimeFormError(error);
              dispatch(removeAllValidationErrorNotifications());
              dispatch(
                pushNotification({
                  body: error,
                  notificationType: NotificationType.importantValidationError,
                }),
              );
            }}
          />
        </GoAFormItem>
      </div>

      <WizardButtonGroup
        nextPage={nextPage}
        validate={(): boolean => {
          return validate();
        }}
        goToReview={navigationConfiguration.goToReview}
        save={save}
        navigate={navigationConfiguration.navigate}
      />
    </div>
  );
};
